import { Badge, Card, SectionHead } from '@codezo/quote-detail';
import type { ReactNode } from 'react';
import type { Tone } from './types';

export interface OrderTransaction {
	key: string;
	/** "Product payment", "Sample payment". */
	title: string;
	status: { label: string; tone: Tone };
	/** The glyph — a card for Stripe, a document for an invoice. */
	icon?: ReactNode;
	/**
	 * The quiet row under the title: method, the invoice link, the date. Nodes,
	 * because the invoice is a button and the rest is text.
	 */
	meta?: ReactNode[];
	/** What this payment was for — one entry per sample in a batch, or per line. */
	notes?: ReactNode[];
	/**
	 * The amount, IN THE CURRENCY IT SETTLED IN. See the section's note: this is
	 * the one place on the page that does not follow the currency selector.
	 */
	amount: ReactNode;
	/** Admin controls for this row — "Mark invoice paid". */
	action?: ReactNode;
}

/**
 * Every payment that has landed.
 *
 * ── Why the money here doesn't match the rest of the page ──
 *
 * A transaction is a historical fact. It was settled in a particular currency on
 * a particular day, and re-expressing it in whatever the viewer has selected
 * would state something that never happened — and would drift as rates move. So
 * these amounts stay in their own currency while every other figure on the page
 * converts, which is exactly why this section carries a note saying so, and why
 * the invoice list the design drew inside the order-total card lives here
 * instead: one card cannot hold both conventions without lying about one.
 *
 * ── Rows are given, not filtered ──
 *
 * The API returns superseded attempts — cancelled and failed invoices — flagged
 * as history. Dropping them is the caller's call, not this module's, because
 * "show me what actually happened" and "show me everything that was tried" are
 * both legitimate and only the page knows which it is offering.
 */
export function OrderTransactions({
	transactions,
	loading,
	note = 'Each amount is in the currency it was settled in.',
	emptyState,
	action,
}: {
	transactions: OrderTransaction[];
	loading?: boolean;
	note?: ReactNode;
	emptyState?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<section>
			<SectionHead
				title="Transactions"
				meta={transactions.length > 0 ? note : undefined}
				action={action}
			/>
			<Card>
				{loading ? (
					<div className="flex flex-col gap-2 p-4">
						{[0, 1, 2].map((i) => (
							<div
								key={i}
								className="h-14 animate-pulse rounded-lg bg-surface-fill"
							/>
						))}
					</div>
				) : transactions.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-text-4">
						{emptyState ?? 'No payments yet.'}
					</div>
				) : (
					transactions.map((txn) => (
						<div
							key={txn.key}
							className="flex items-start gap-3 border-b border-line-1 px-4 py-3.5 last:border-b-0"
						>
							{txn.icon && (
								<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill text-text-4">
									{txn.icon}
								</span>
							)}
							<div className="min-w-0 flex-1">
								<div className="mb-0.5 flex flex-wrap items-center gap-2">
									<span className="text-sm font-medium text-text-1">
										{txn.title}
									</span>
									<Badge tone={txn.status.tone} dot>
										{txn.status.label}
									</Badge>
								</div>
								{txn.meta && txn.meta.length > 0 && (
									<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-4">
										{txn.meta.map((entry, i) => (
											<span key={i}>{entry}</span>
										))}
									</div>
								)}
								{txn.notes && txn.notes.length > 0 && (
									<div className="mt-1.5 flex flex-col gap-0.5 text-xs text-text-3">
										{txn.notes.map((entry, i) => (
											<span key={i}>{entry}</span>
										))}
									</div>
								)}
								{txn.action && <div className="mt-2">{txn.action}</div>}
							</div>
							<span className="shrink-0 font-mono text-sm font-medium text-text-1">
								{txn.amount}
							</span>
						</div>
					))
				)}
			</Card>
		</section>
	);
}
